import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, DatabaseBackup, Download, Upload, Loader2, CheckCircle2, AlertTriangle,
  FileCheck2, Clock, Lock, History, X, ChevronRight, ChevronLeft, ListChecks, Calendar,
  Server, FlaskConical, RefreshCw, ExternalLink, Globe, Copy, ShieldAlert, FileSearch,
  HardDrive, KeyRound, Cloud, Repeat, Layers, Zap, Timer, Camera, RotateCcw,
} from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Card } from "@/components/common";
import { useI18n } from "@/context/I18nContext";

const COLL_LABEL = {
  users: "Benutzer", clients: "Kunden", websites: "Websites", subscriptions: "Abos",
  packages: "Pakete", modules: "Module", client_modules: "Kunden-Module", tasks: "Aufgaben",
  leads: "Anfragen", support_messages: "Support-Nachrichten", support_tickets: "Supportfälle",
  invoices: "Rechnungen", reports: "Reports", next_steps: "Nächste Schritte", client_analytics: "Analytics",
  settings: "Einstellungen", translations: "Übersetzungen", activity_log: "Protokoll", security_log: "Sicherheits-Log",
};
const KEY_DATA = ["clients", "users", "leads", "tasks", "invoices", "packages", "settings", "reports", "activity_log", "websites"];
const TYPE_LABEL = {
  manual: "Manuell", emergency: "Notfall", auto: "Automatisch",
  auto_daily: "Automatisch (täglich)", auto_weekly: "Automatisch (wöchentlich)", auto_monthly: "Automatisch (monatlich)",
};
const STATUS_LABEL = { success: "Erfolgreich", failed: "Fehlgeschlagen" };
const PER_PAGE = 15;

const RP_TRIGGER = {
  interval: "Automatisch (Änderung erkannt)",
  pre_critical: "Vor kritischer Aktion",
  manual: "Manuell erstellt",
};
const RP_ICON = { interval: Timer, pre_critical: Zap, manual: Camera };

const WP_TONE = {
  green: { dot: "#A3E635", text: "text-[#A3E635]", chip: "bg-[#A3E635]/10 border-[#A3E635]/30 text-[#A3E635]" },
  amber: { dot: "#f59e0b", text: "text-amber-400", chip: "bg-amber-500/10 border-amber-500/30 text-amber-300" },
  red: { dot: "#ef4444", text: "text-red-400", chip: "bg-red-500/10 border-red-500/30 text-red-300" },
  gray: { dot: "#6b7280", text: "text-gray-400", chip: "bg-white/5 border-white/10 text-gray-400" },
};

const pad = (n) => String(n).padStart(2, "0");
function smartDate(iso) {
  if (!iso) return "Noch nie";
  const d = new Date(iso), now = new Date();
  const same = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const tmrw = new Date(now); tmrw.setDate(now.getDate() + 1);
  const yest = new Date(now); yest.setDate(now.getDate() - 1);
  const t = `${pad(d.getHours())}:${pad(d.getMinutes())} Uhr`;
  if (same(d, now)) return `Heute, ${t}`;
  if (same(d, tmrw)) return `Morgen, ${t}`;
  if (same(d, yest)) return `Gestern, ${t}`;
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}, ${t}`;
}
function fmtSize(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
}
function triggerDownload(obj, name) {
  const url = URL.createObjectURL(new Blob([JSON.stringify(obj)], { type: "application/json" }));
  const a = document.createElement("a");
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export default function Backup() {
  const { t: tr } = useI18n();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [wp, setWp] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [detailsId, setDetailsId] = useState(null);
  const [checkingWp, setCheckingWp] = useState(null);
  const [offsiteTesting, setOffsiteTesting] = useState(false);
  const [page, setPage] = useState(1);
  const [verifying, setVerifying] = useState(false);

  // Restore-Points
  const [rp, setRp] = useState(null);
  const [rpBusy, setRpBusy] = useState(null);
  const [rpDetailId, setRpDetailId] = useState(null);
  const [showAllRp, setShowAllRp] = useState(false);
  const [rpRestore, setRpRestore] = useState(null);
  const [rpPw, setRpPw] = useState(""); const [rpCode, setRpCode] = useState("");
  const [rpConfirm, setRpConfirm] = useState(""); const [rpUnderstand, setRpUnderstand] = useState(false);
  const [rpRestoreBusy, setRpRestoreBusy] = useState(false);

  const testOffsite = async () => {
    setOffsiteTesting(true);
    try {
      const { data: r } = await api.post("/admin/backups/offsite-test");
      if (r.ok) toast.success(tr("Externer Speicher erreichbar — Schreibtest erfolgreich"));
      else toast.error(tr("Externer Speicher: {x}").replace("{x}", r.error || tr("Fehler")));
    } catch (e) { toast.error(e.response?.data?.detail || tr("Test fehlgeschlagen")); }
    finally { setOffsiteTesting(false); }
  };

  const verifyLatest = async () => {
    setVerifying(true);
    try {
      const { data: r } = await api.post("/admin/backups/verify-latest");
      await load();
      if (r.status === "verified") toast.success(tr("Letztes Backup erfolgreich geprüft · Integrität bestätigt"));
      else if (r.status === "none") toast.error(tr("Kein Backup vorhanden."));
      else toast.error(tr("Integritätsprüfung fehlgeschlagen: {x}").replace("{x}", (r.errors || []).join("; ") || tr("Fehler")));
    } catch (e) { toast.error(e.response?.data?.detail || tr("Prüfung fehlgeschlagen")); }
    finally { setVerifying(false); }
  };

  // Download-Sicherheitsmodal
  const [dlModal, setDlModal] = useState(null);
  const [dlPw, setDlPw] = useState(""); const [dlCode, setDlCode] = useState(""); const [dlBusy, setDlBusy] = useState(false);

  // Restore-Assistent
  const [wizard, setWizard] = useState(0);
  const [restoreFile, setRestoreFile] = useState(null);
  const [payload, setPayload] = useState(null);
  const [verifyRes, setVerifyRes] = useState(null);
  const [sandboxRes, setSandboxRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [pw, setPw] = useState(""); const [code, setCode] = useState(""); const [understand, setUnderstand] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/backups").then((r) => setData(r.data));
  const loadWp = () => api.get("/admin/wp-backups").then((r) => setWp(r.data)).catch(() => {});
  const loadRp = () => api.get("/admin/restore-points").then((r) => setRp(r.data)).catch(() => {});
  useEffect(() => { load(); loadWp(); loadRp(); }, []);

  const createBackup = async () => {
    setCreating(true);
    try {
      const { data: meta } = await api.post("/admin/backups");
      setCreateResult(meta); setPage(1); await load();
      toast.success(tr("Backup erfolgreich erstellt · Prüfsumme erzeugt"));
      return meta;
    } catch (e) { toast.error(e.response?.data?.detail || tr("Backup fehlgeschlagen")); }
    finally { setCreating(false); }
  };

  // --- Restore-Points ---
  const createRp = async () => {
    setRpBusy("create");
    try {
      await api.post("/admin/restore-points/create");
      await loadRp();
      toast.success(tr("Restore-Point erstellt · Integrität geprüft"));
    } catch (e) { toast.error(e.response?.data?.detail || tr("Restore-Point fehlgeschlagen")); }
    finally { setRpBusy(null); }
  };
  const verifyRp = async (id) => {
    setRpBusy(id);
    try {
      const { data: r } = await api.post(`/admin/restore-points/${id}/verify`);
      await loadRp();
      if (r.status === "verified") toast.success(tr("Restore-Point geprüft · Integrität bestätigt"));
      else toast.error(tr("Prüfung fehlgeschlagen: {x}").replace("{x}", (r.errors || []).join("; ") || tr("Fehler")));
    } catch (e) { toast.error(e.response?.data?.detail || tr("Prüfung fehlgeschlagen")); }
    finally { setRpBusy(null); }
  };
  const openRpRestore = (id) => { setRpRestore(id); setRpPw(""); setRpCode(""); setRpConfirm(""); setRpUnderstand(false); };
  const runRpRestore = async () => {
    if (rpConfirm.trim().toUpperCase() !== "WIEDERHERSTELLEN") return toast.error(tr("Bitte WIEDERHERSTELLEN eingeben"));
    if (!rpPw) return toast.error(tr("Bitte Passwort eingeben"));
    if (!rpUnderstand) return toast.error(tr("Bitte bestätigen Sie das Überschreiben"));
    setRpRestoreBusy(true);
    try {
      await api.post(`/admin/restore-points/${rpRestore}/restore`, { password: rpPw, code: rpCode || undefined, confirm_text: rpConfirm, understand: true });
      toast.success(tr("Wiederherstellung erfolgreich – Seite wird neu geladen"));
      setTimeout(() => window.location.reload(), 1800);
    } catch (e) { toast.error(e.response?.data?.detail || tr("Wiederherstellung fehlgeschlagen")); setRpRestoreBusy(false); }
  };

  // --- Sicherer Download (Passwort + 2FA) ---
  const openDownload = (id) => { setDlModal(id); setDlPw(""); setDlCode(""); };
  const runDownload = async () => {
    if (!dlPw) return toast.error(tr("Bitte Passwort eingeben"));
    setDlBusy(true);
    try {
      let id = dlModal;
      if (id === "full") {
        const meta = data?.last_successful || data?.last_backup || (await createBackup());
        if (!meta) throw new Error("kein Backup");
        id = meta.id;
      }
      const { data: prep } = await api.post(`/admin/backups/${id}/prepare-download`, { password: dlPw, code: dlCode || undefined });
      const { data: file } = await api.get(`/admin/backups/${id}/download`, { params: { dt: prep.download_token } });
      triggerDownload(file, `kewagpage-backup-${String(id).slice(0, 8)}.json`);
      toast.success(tr("Backup sicher heruntergeladen (Secrets AES-256-verschlüsselt)"));
      setDlModal(null);
    } catch (e) { toast.error(e.response?.data?.detail || tr("Download fehlgeschlagen")); }
    finally { setDlBusy(false); }
  };

  // --- Restore-Assistent ---
  const openWizard = () => { resetWizard(); setWizard(1); setTimeout(() => fileRef.current?.click(), 120); };
  const resetWizard = () => { setRestoreFile(null); setPayload(null); setVerifyRes(null); setSandboxRes(null); setConfirmText(""); setPw(""); setCode(""); setUnderstand(false); };
  const closeWizard = () => { if (busy) return; setWizard(0); resetWizard(); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    try {
      const p = JSON.parse(await file.text());
      if (!p.collections) throw new Error("invalid");
      setRestoreFile(file); setPayload(p); setWizard(2); setVerifyRes(null); setSandboxRes(null);
      runVerify(p);
    } catch { toast.error(tr("Ungültige Backup-Datei (kein gültiges JSON-Format)")); }
  };

  const runVerify = async (p) => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/restore/preview", p || payload);
      setVerifyRes(r);
    } catch (e) { toast.error(e.response?.data?.detail || tr("Prüfung fehlgeschlagen")); }
    finally { setBusy(false); }
  };

  const runSandbox = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/restore/sandbox", payload);
      setSandboxRes(r);
      toast.success(tr("Sandbox-Test erfolgreich · {n} Datensätze testweise importiert").replace("{n}", r.total));
    } catch (e) { toast.error(e.response?.data?.detail || tr("Sandbox-Test fehlgeschlagen")); }
    finally { setBusy(false); }
  };

  const runRestore = async () => {
    if (confirmText.trim().toUpperCase() !== "WIEDERHERSTELLEN") return toast.error(tr("Bitte WIEDERHERSTELLEN eingeben"));
    if (!understand) return toast.error(tr("Bitte bestätigen Sie das Überschreiben"));
    if (!pw) return toast.error(tr("Bitte Passwort eingeben"));
    setBusy(true);
    try {
      await api.post("/admin/restore", {
        collections: payload.collections, checksum_sha256: payload.checksum_sha256,
        format_version: payload.format_version || payload.version, app_version: payload.app_version,
        password: pw, code: code || undefined, confirm_text: confirmText, understand: true,
      });
      toast.success(tr("Wiederherstellung erfolgreich – Seite wird neu geladen"));
      setTimeout(() => window.location.reload(), 1800);
    } catch (e) { toast.error(e.response?.data?.detail || tr("Wiederherstellung fehlgeschlagen")); setBusy(false); }
  };

  const checkWp = async (cid) => {
    setCheckingWp(cid);
    try { await api.post(`/websites/${cid}/check`); await loadWp(); toast.success(tr("WordPress-Backup live geprüft")); }
    catch (e) { toast.error(e.response?.data?.detail || tr("Prüfung fehlgeschlagen")); }
    finally { setCheckingWp(null); }
  };

  if (!data) return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#A3E635]" /></div>;

  const last = data.last_successful || data.last_backup;
  const ok = data.last_successful && data.last_successful.status === "success";
  const integ = data.integrity;
  const offsite = data.offsite_storage || {};
  const icStatus = integ?.status; // verified | failed | needs_check (oder null)

  // Restore-Point-Daten
  const lastRp = rp?.last_restore_point;
  const lastFull = rp?.last_full_auto || data.last_successful;
  const rpActive = !!rp?.active;
  const preCriticalActive = !!rp?.pre_critical_active;
  const rpPoints = rp?.points || [];
  const rpVisible = showAllRp ? rpPoints : rpPoints.slice(0, 10);

  // Wiederherstellungs-Bereitschaft (ehrlich: grün NUR nach erfolgreicher Integritätsprüfung)
  const READY = {
    verified: { value: tr("Erfolgreich geprüft"), accent: "lime", pill: tr("Geprüft"), tone: "green" },
    needs_check: { value: tr("Prüfung nötig"), accent: "amber", pill: tr("Prüfung nötig"), tone: "amber" },
    failed: { value: tr("Fehler"), accent: "red", pill: tr("Fehler"), tone: "red" },
    none: { value: tr("Kein Backup"), accent: "gray", pill: tr("Kein Backup"), tone: "gray" },
  };
  const readyKey = !last ? "none" : (icStatus === "verified" ? "verified" : icStatus === "failed" ? "failed" : "needs_check");
  const ready = READY[readyKey];

  // Backup-Historie Pagination
  const allBackups = data.backups || [];
  const totalPages = Math.max(1, Math.ceil(allBackups.length / PER_PAGE));
  const curPage = Math.min(page, totalPages);
  const pageItems = allBackups.slice((curPage - 1) * PER_PAGE, curPage * PER_PAGE);

  return (
    <div className="fade-in w-full" data-testid="backup-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHeader title={tr("Backup & Wiederherstellung")}
          subtitle={tr("Sichert, dokumentiert und jederzeit wiederherstellbar. Getrennt von den WordPress-Backups der Kunden-Websites. Nur für Super Admins.")} />
        <button onClick={() => nav("/admin/audit-log")} data-testid="backup-audit-link"
          className="mt-1 bg-white/5 hover:bg-white/10 border border-white/10 py-2 px-4 rounded-lg text-sm flex items-center gap-2 shrink-0 transition-colors">
          <FileSearch className="w-4 h-4" /> {tr("Audit-Log anzeigen")}
        </button>
      </div>

      {/* ===== System-Schutz (Restore-Point-Übersicht) ===== */}
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-gray-400 mb-3 mt-1">
        <ShieldCheck className="w-4 h-4 text-[#A3E635]" /> {tr("System-Schutz")}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="backup-status-grid">
        <StatusTile testid="tile-protection" icon={ShieldCheck} label={tr("Systemschutz")}
          value={ok ? tr("Geschützt") : tr("Noch kein Backup")} sub={ok ? tr("Daten gesichert & geprüft") : tr("Erstes Backup erstellen")}
          accent={ok ? "lime" : "amber"} pill={ok ? tr("Aktiv") : tr("Prüfung nötig")} tone={ok ? "green" : "amber"} />
        <StatusTile testid="tile-restore-points" icon={Layers} label={tr("Restore-Points")}
          value={rpActive ? tr("Aktiv") : tr("Inaktiv")}
          sub={rpActive ? tr("Alle {m} Min · nur bei Änderung").replace("{m}", rp?.interval_minutes || 15) : tr("Nicht aktiv")}
          accent={rpActive ? "lime" : "gray"} pill={rpActive ? tr("Aktiv") : tr("Inaktiv")} tone={rpActive ? "green" : "gray"} />
        <StatusTile testid="tile-precritical" icon={Zap} label={tr("Backup vor kritischen Aktionen")}
          value={preCriticalActive ? tr("Aktiv") : tr("Inaktiv")} sub={tr("Sicherungspunkt vor Löschung & Wiederherstellung")}
          accent={preCriticalActive ? "lime" : "gray"} pill={preCriticalActive ? tr("Aktiv") : tr("Inaktiv")} tone={preCriticalActive ? "green" : "gray"} />
        <StatusTile testid="tile-last-rp" icon={Camera} label={tr("Letzter Restore-Point")}
          value={smartDate(lastRp?.created_at)} sub={lastRp ? tr(RP_TRIGGER[lastRp.trigger] || lastRp.trigger) : tr("Noch keiner")} />
        <StatusTile testid="tile-next-rp" icon={Timer} label={tr("Nächster Restore-Point")}
          value={smartDate(rp?.next_check)} sub={tr("Nur bei Änderung")} />
        <StatusTile testid="tile-last-full" icon={Clock} label={tr("Letzte Vollsicherung")}
          value={smartDate(lastFull?.created_at)} sub={lastFull ? `${tr(TYPE_LABEL[lastFull.type] || lastFull.type)} · ${fmtSize(lastFull.size_bytes)}` : tr("Keine")} />
      </div>

      {/* ===== Wiederherstellungs-Bereitschaft & Integritätsprüfung (breite Sicherheitskarte) ===== */}
      <div className="bg-[#121217] border border-white/10 rounded-xl p-6 mb-8" data-testid="readiness-card">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6">
          {/* Status */}
          <div className="flex items-start gap-4 lg:w-[320px] shrink-0">
            <div className="flex flex-col items-center gap-1.5 rounded-xl bg-black/60 border border-white/10 px-2.5 py-2.5 shrink-0"
                 data-testid="backup-ampel" data-status={readyKey} title={tr("Selbsttest-Ampel: Grün = zuletzt erfolgreich geprüft")}>
              <span data-testid="backup-ampel-red" className={`w-3.5 h-3.5 rounded-full transition-all duration-300 ${ready.tone === "red" ? "bg-red-500 shadow-[0_0_10px_2px_rgba(239,68,68,0.75)]" : "bg-red-500/20"}`} />
              <span data-testid="backup-ampel-amber" className={`w-3.5 h-3.5 rounded-full transition-all duration-300 ${ready.tone === "amber" ? "bg-amber-400 shadow-[0_0_10px_2px_rgba(251,191,36,0.75)]" : "bg-amber-400/20"}`} />
              <span data-testid="backup-ampel-green" className={`w-3.5 h-3.5 rounded-full transition-all duration-300 ${ready.tone === "green" ? "bg-[#A3E635] shadow-[0_0_10px_2px_rgba(163,230,53,0.75)]" : "bg-[#A3E635]/20"}`} />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-gray-400">{tr("Wiederherstellungs-Bereitschaft")}</div>
              <div className={`text-2xl font-black tracking-tight mt-0.5 ${TILE_ACCENT[ready.accent]}`} data-testid="readiness-value">{ready.value}</div>
              <div className="text-xs mt-1" data-testid="readiness-last-check">
                {readyKey === "verified" ? (
                  <span className="text-[#A3E635]">{tr("Zuletzt erfolgreich geprüft")}: {smartDate(integ.checked_at)}{integ?.checked_by ? ` · ${integ.checked_by}` : ""}</span>
                ) : readyKey === "failed" ? (
                  <span className="text-red-300">{tr("Letzte Prüfung fehlgeschlagen")}: {smartDate(integ?.checked_at)}</span>
                ) : readyKey === "none" ? (
                  <span className="text-gray-500">{tr("Noch kein Backup vorhanden")}</span>
                ) : (
                  <span className="text-amber-300">{tr("Noch nicht geprüft — bitte jetzt prüfen")}</span>
                )}
              </div>
            </div>
          </div>

          {/* Prüfumfang / Checkliste */}
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 lg:border-l lg:border-white/10 lg:pl-6" data-testid="readiness-checks">
            <IntegrityCheck label={tr("Backup vorhanden")} state={last ? "ok" : "none"} />
            <IntegrityCheck label={tr("Datei lesbar & Prüfsumme (SHA-256)")} state={checkState(integ?.checksum_valid)} />
            <IntegrityCheck label={tr("Verschlüsselung entschlüsselbar (AES-256-GCM)")} state={checkState(integ?.decrypt_ok)} />
            <IntegrityCheck label={tr("Metadaten vollständig")} state={checkState(integ?.metadata_ok)} />
          </div>

          {/* Aktion */}
          <div className="lg:w-[230px] shrink-0">
            <button onClick={verifyLatest} disabled={verifying || !last} data-testid="verify-latest-btn"
              className="w-full bg-[#A3E635] hover:bg-[#b6f15a] text-black font-bold py-2.5 px-4 rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-colors">
              {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} {tr("Letztes Backup prüfen")}
            </button>
            <p className="text-[11px] text-gray-500 mt-2 leading-snug">{tr("Prüft das letzte Backup ohne Wiederherstellung. Grün nur nach erfolgreicher Prüfung.")}</p>
            <p className="text-[11px] text-gray-600 mt-1 leading-snug" data-testid="readiness-auto-note">{tr("Läuft auch automatisch nach jedem nächtlichen Backup (02:00).")}</p>
          </div>
        </div>

        {integ?.status === "needs_check" && (
          <div className="mt-4 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="readiness-needs-check">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {tr("Das aktuellste Backup wurde noch nicht geprüft. Bitte jetzt prüfen, um die Integrität ehrlich zu bestätigen.")}
          </div>
        )}
        {integ?.status === "failed" && (integ?.errors?.length > 0) && (
          <div className="mt-4 text-xs text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 space-y-1" data-testid="readiness-errors">
            {integ.errors.map((e, i) => <div key={i} className="flex items-start gap-2"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{e}</div>)}
          </div>
        )}
      </div>

      {/* ===== Restore-Point-Timeline ===== */}
      <Card className="p-0 overflow-hidden mb-8" data-testid="restore-point-timeline">
        <div className="px-5 py-3.5 border-b border-white/10 flex items-center justify-between gap-2 flex-wrap">
          <div className="font-bold tracking-tight text-sm flex items-center gap-2">
            <Layers className="w-4 h-4 text-[#A3E635]" /> {tr("Restore-Point-Timeline")}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {rp && <span className="text-[11px] text-gray-500">{tr("{n} Punkte · {i} automatisch · {p} kritisch").replace("{n}", rp.total).replace("{i}", rp.summary?.interval || 0).replace("{p}", rp.summary?.pre_critical || 0)}</span>}
            <button onClick={createRp} disabled={rpBusy === "create"} data-testid="rp-create-btn"
              className="bg-white/5 hover:bg-white/10 border border-white/10 py-1.5 px-3 rounded-lg text-xs flex items-center gap-1.5 disabled:opacity-50 transition-colors">
              {rpBusy === "create" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />} {tr("Jetzt sichern")}
            </button>
          </div>
        </div>
        <div className="px-5 py-2.5 text-[11px] text-gray-500 bg-white/[0.02] border-b border-white/5">
          {tr("Restore-Points sind verschlüsselte, komprimierte Sicherungspunkte – getrennt von den täglichen Vollbackups. Automatisch alle 15 Min (nur bei Änderung) und vor kritischen Aktionen. Aufbewahrung: 24 h, danach automatisch ausgedünnt.")}
        </div>
        {!rp ? (
          <div className="p-6"><Loader2 className="w-5 h-5 animate-spin text-[#A3E635]" /></div>
        ) : rpPoints.length === 0 ? (
          <div className="px-5 py-8 text-center text-gray-500 text-sm" data-testid="rp-empty">
            {tr("Noch keine Restore-Points. Der erste entsteht automatisch bei der nächsten Datenänderung oder per «Jetzt sichern».")}
          </div>
        ) : (
          <div className="p-5">
            <div className="relative pl-6">
              <div className="absolute left-[7px] top-1 bottom-1 w-px bg-white/10" />
              {rpVisible.map((p) => {
                const ic = p.integrity_check;
                const tone = ic?.status === "verified" ? "green" : ic?.status === "failed" ? "red" : "gray";
                const dot = WP_TONE[tone].dot;
                const Tico = RP_ICON[p.trigger] || Camera;
                const open = rpDetailId === p.id;
                return (
                  <div key={p.id} className="relative mb-4 last:mb-0" data-testid={`rp-item-${p.id}`}>
                    <span className="absolute -left-6 top-2 w-3.5 h-3.5 rounded-full border-2 border-[#0a0a0f]" style={{ background: dot }} />
                    <div className="bg-[#121217] border border-white/10 rounded-xl p-3.5">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${p.trigger === "pre_critical" ? "bg-amber-500/10 border-amber-500/30 text-amber-300" : p.trigger === "manual" ? "bg-blue-500/10 border-blue-500/30 text-blue-200" : "bg-[#A3E635]/10 border-[#A3E635]/30 text-[#A3E635]"}`}>
                              <Tico className="w-3 h-3" /> {tr(RP_TRIGGER[p.trigger] || p.trigger)}
                            </span>
                            <span className="text-sm font-semibold">{smartDate(p.created_at)}</span>
                          </div>
                          <div className="text-xs text-gray-400 mt-1 truncate">{p.reason}</div>
                          <div className="text-[11px] text-gray-500 mt-0.5">
                            {fmtSize(p.size_bytes)} · {tr("komprimiert & verschlüsselt")} ·{" "}
                            <span className={ic?.status === "verified" ? "text-[#A3E635]" : ic?.status === "failed" ? "text-red-400" : "text-gray-400"}>
                              {ic?.status === "verified" ? tr("Geprüft") : ic?.status === "failed" ? tr("Fehler") : tr("Nicht geprüft")}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => setRpDetailId(open ? null : p.id)} data-testid={`rp-details-${p.id}`}
                            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors" title={tr("Details anzeigen")}><ListChecks className="w-4 h-4" /></button>
                          <button onClick={() => verifyRp(p.id)} disabled={rpBusy === p.id} data-testid={`rp-verify-${p.id}`}
                            className="p-2 rounded-lg text-gray-400 hover:text-[#A3E635] hover:bg-white/5 transition-colors disabled:opacity-50" title={tr("Integrität prüfen")}>
                            {rpBusy === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                          </button>
                          <button onClick={() => openRpRestore(p.id)} data-testid={`rp-restore-${p.id}`}
                            className="p-2 rounded-lg text-gray-400 hover:text-amber-400 hover:bg-white/5 transition-colors" title={tr("Aus diesem Punkt wiederherstellen")}><RotateCcw className="w-4 h-4" /></button>
                        </div>
                      </div>
                      {open && (
                        <div className="mt-3 pt-3 border-t border-white/10 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs" data-testid={`rp-detail-row-${p.id}`}>
                          {Object.entries(p.counts || {}).filter(([k]) => KEY_DATA.includes(k)).map(([k, v]) => (
                            <div key={k} className="flex justify-between"><span className="text-gray-500">{tr(COLL_LABEL[k] || k)}</span><span className="font-mono">{v}</span></div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {rpPoints.length > 10 && (
              <button onClick={() => setShowAllRp((s) => !s)} data-testid="rp-show-all"
                className="mt-2 w-full bg-white/5 hover:bg-white/10 border border-white/10 py-2 rounded-lg text-xs flex items-center justify-center gap-2 transition-colors">
                {showAllRp ? tr("Weniger anzeigen") : tr("Alle anzeigen ({n})").replace("{n}", rpPoints.length)}
              </button>
            )}
          </div>
        )}
      </Card>

      {/* ===== Aktionen ===== */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-4" data-testid="backup-actions">
        <ActionCard testid="backup-create" icon={DatabaseBackup} tone="lime" title={tr("Backup jetzt erstellen")}
          desc={tr("Erstellt sofort ein vollständiges, verschlüsseltes Systembackup.")}
          btn={creating ? tr("Wird erstellt…") : tr("Backup jetzt erstellen")} busy={creating} onClick={createBackup} />
        <ActionCard testid="backup-download" icon={Download} tone="default" title={tr("Voll-Backup herunterladen")}
          desc={tr("Nur Super Admin. Vor dem Download ist eine Sicherheitsbestätigung nötig.")}
          btn={tr("Backup herunterladen")} onClick={() => openDownload("full")} />
        <ActionCard testid="restore-prepare" icon={Upload} tone="amber" title={tr("Wiederherstellung vorbereiten")}
          desc={tr("Startet den 4-Schritte-Assistenten: prüfen, testen, bestätigen.")}
          btn={tr("Wiederherstellung vorbereiten")} onClick={openWizard} />
        <ActionCard testid="restore-sandbox-open" icon={FlaskConical} tone="blue" title={tr("Wiederherstellung testen")}
          desc={tr("Test-Wiederherstellung in isolierter Sandbox – Live-Daten bleiben unberührt.")}
          btn={tr("Sandbox-Test starten")} onClick={openWizard} />
      </div>

      {createResult && (
        <div className="mb-4 p-4 rounded-xl bg-[#A3E635]/5 border border-[#A3E635]/20 flex items-center gap-3 flex-wrap" data-testid="backup-create-result">
          <CheckCircle2 className="w-5 h-5 text-[#A3E635] shrink-0" />
          <div className="text-sm">
            <div className="font-semibold text-[#A3E635]">{tr("Backup erfolgreich erstellt")}</div>
            <div className="text-xs text-gray-400">{smartDate(createResult.created_at)} · {fmtSize(createResult.size_bytes)} · SHA-256 {String(createResult.checksum_sha256 || "").slice(0, 12)}…</div>
          </div>
          <button onClick={() => openDownload(createResult.id)} data-testid="backup-create-download"
            className="ml-auto bg-white/5 hover:bg-white/10 border border-white/10 py-2 px-4 rounded-lg text-sm flex items-center gap-2 transition-colors">
            <Download className="w-4 h-4" /> {tr("Sicher herunterladen")}
          </button>
        </div>
      )}

      {/* ===== Konfiguration: Zeitplan / Ziele / EU-Speicher / Integrität / Daten / Zugriff ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
        {/* Backup-Status & Zeitplan */}
        <Panel icon={Repeat} title={tr("Backup-Status & Zeitplan")} testid="panel-schedule">
          <KV k={tr("Letztes Backup")} v={smartDate(last?.created_at)} />
          <KV k={tr("Nächstes geplantes Backup")} v={smartDate(data.next_scheduled)} good />
          <KV k={tr("Backup-Frequenz")} v={tr("Täglich (02:00)")} />
          <KV k={tr("Aufbewahrung")} v={tr("Täglich / 30 Tage")} />
          <div className="text-[11px] text-gray-500 pt-1">
            {tr("Wöchentlich {w} W · Monatlich {m} M").replace("{w}", data.retention?.auto_weekly || 12).replace("{m}", data.retention?.auto_monthly || 12)}
          </div>
        </Panel>

        {/* Backup-Ziele & Verschlüsselung */}
        <Panel icon={Server} title={tr("Backup-Ziele & Verschlüsselung")} testid="backup-targets">
          <div className="space-y-2.5 mb-3">
            {(data.targets || []).map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-2" data-testid={`target-${t.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{t.name}</div>
                  <div className="text-[11px] text-gray-500 truncate">{t.location}</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border shrink-0 ${t.status === "active" ? "bg-[#A3E635]/10 border-[#A3E635]/30 text-[#A3E635]" : "bg-white/5 border-white/10 text-gray-400"}`}>{tr(t.label)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-white/10 pt-3 space-y-1.5 text-xs">
            <KV k={tr("Verschlüsselung (Secrets)")} v={data.encryption || "AES-256-GCM"} good />
            <KV k={tr("Übertragung")} v={tr("HTTPS / TLS")} good />
            <KV k={tr("Prüfsumme")} v="SHA-256" good />
          </div>
        </Panel>

        {/* Externer EU-Speicher */}
        <Panel icon={Cloud} title={tr("Externer EU-Speicher")} testid="backup-offsite">
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border ${offsite.enabled ? "bg-[#A3E635]/10 border-[#A3E635]/30 text-[#A3E635]" : "bg-white/5 border-white/10 text-gray-400"}`} data-testid="offsite-status-badge">
              {offsite.enabled ? tr("Aktiv") : tr("Vorbereitet")}
            </span>
            {offsite.enabled && <span className="text-[11px] text-gray-400 truncate">{offsite.bucket} · {offsite.region}</span>}
          </div>
          <p className="text-[11px] text-gray-500 mt-2 flex-1">
            {offsite.enabled
              ? tr("Jedes Backup wird zusätzlich verschlüsselt in den externen EU-Speicher geladen.")
              : tr("Noch keine Zugangsdaten hinterlegt. Setzen Sie S3_BACKUP_BUCKET / _KEY / _SECRET (optional _ENDPOINT für Hetzner & Co.), um Backups zusätzlich extern in der EU zu sichern.")}
          </p>
          <button onClick={testOffsite} disabled={offsiteTesting || !offsite.enabled} data-testid="offsite-test-btn"
            className="mt-3 w-full bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm font-semibold py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 transition-colors">
            {offsiteTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} {tr("Verbindung testen")}
          </button>
        </Panel>

        {/* Gesicherte Daten */}
        <Panel icon={ListChecks} title={tr("Gesicherte Daten")} testid="status-data">
          <div className="flex flex-wrap gap-2">
            {KEY_DATA.map((k) => (
              <span key={k} className="text-xs px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-gray-300">{tr(COLL_LABEL[k])}</span>
            ))}
          </div>
        </Panel>

        {/* Zugriff & Sicherheit */}
        <Panel icon={Lock} title={tr("Zugriff & Sicherheit")} testid="status-access">
          <div className="text-lg font-bold">{tr("Nur Super Admin")}</div>
          <div className="text-xs text-gray-500 mt-1">{tr("Wiederherstellungen erfordern Passwort + Zwei-Faktor und sind protokolliert.")}</div>
          <div className="mt-2 flex items-center gap-2 text-xs text-[#A3E635]"><span className="w-1.5 h-1.5 rounded-full bg-[#A3E635]" /> {tr("Alle Aktionen werden im Audit-Log erfasst")}</div>
        </Panel>
      </div>

      {/* ===== WordPress-Kunden-Backups (separate Tabelle) ===== */}
      <Card className="p-0 overflow-hidden mb-8" data-testid="wp-backups">
        <div className="px-5 py-3.5 border-b border-white/10 flex items-center justify-between gap-2 flex-wrap">
          <div className="font-bold tracking-tight text-sm flex items-center gap-2"><Globe className="w-4 h-4 text-[#A3E635]" /> {tr("WordPress-Backups der Kunden-Websites")}</div>
          {wp && <div className="text-[11px] text-gray-500">{tr("{green} live · {needs} prüfen · {gray} nicht verbunden").replace("{green}", wp.summary.green).replace("{needs}", wp.summary.needs_attention).replace("{gray}", wp.summary.gray)}</div>}
        </div>
        <div className="px-5 py-2.5 text-[11px] text-gray-500 bg-white/[0.02] border-b border-white/5">
          {tr("Hinweis: Das KEWAGPage-Systembackup ersetzt nicht das WordPress-Backup der Kunden-Website. Nicht live geprüfte Backups werden nie grün angezeigt.")}
        </div>
        {!wp ? <div className="p-6"><Loader2 className="w-5 h-5 animate-spin text-[#A3E635]" /></div> : wp.clients.length === 0 ? (
          <div className="px-5 py-6 text-gray-500 text-sm">{tr("Keine Kunden-Websites vorhanden.")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-gray-500 border-b border-white/5">
                  <th className="text-left font-semibold px-5 py-2.5">{tr("Kunde")}</th>
                  <th className="text-left font-semibold px-3 py-2.5">{tr("WordPress-Backup-Status")}</th>
                  <th className="text-left font-semibold px-3 py-2.5 hidden md:table-cell">{tr("Letzte Prüfung")}</th>
                  <th className="text-right font-semibold px-5 py-2.5">{tr("Aktionen")}</th>
                </tr>
              </thead>
              <tbody>
                {wp.clients.map((c) => {
                  const tone = WP_TONE[c.color] || WP_TONE.gray;
                  return (
                    <tr key={c.client_id} className="border-b border-white/5 last:border-0" data-testid={`wp-row-${c.client_id}`}>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: tone.dot }} />
                          <div className="min-w-0">
                            <div className="font-semibold truncate">{c.company_name}</div>
                            <div className="text-[11px] text-gray-500 truncate">{c.domain || "—"}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3.5">
                        <span className={`text-xs font-semibold ${tone.text}`}>{tr(c.label)}</span>
                        <div className="text-[11px] text-gray-500">{c.connected ? `${tr("Verbunden")}${c.backup_plugin ? " · " + c.backup_plugin : ""}` : tr("Nicht live verbunden")}</div>
                      </td>
                      <td className="px-3 py-3.5 text-gray-400 text-xs whitespace-nowrap hidden md:table-cell">{c.last_backup ? smartDate(c.last_backup) : "—"}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-1.5">
                          <button onClick={() => checkWp(c.client_id)} disabled={checkingWp === c.client_id} data-testid={`wp-check-${c.client_id}`}
                            className="bg-white/5 hover:bg-white/10 border border-white/10 py-1.5 px-3 rounded-lg text-xs flex items-center gap-1.5 disabled:opacity-50 transition-colors">
                            {checkingWp === c.client_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} {tr("Backup prüfen")}
                          </button>
                          {c.website_url && (
                            <a href={c.website_url} target="_blank" rel="noreferrer" data-testid={`wp-open-${c.client_id}`}
                              className="bg-white/5 hover:bg-white/10 border border-white/10 py-1.5 px-3 rounded-lg text-xs flex items-center gap-1.5 transition-colors">
                              <ExternalLink className="w-3.5 h-3.5" /> {tr("Öffnen")}
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ===== Backup-Historie (mit Pagination) ===== */}
      <Card className="p-0 overflow-hidden mb-8" data-testid="backup-history">
        <div className="px-5 py-3.5 border-b border-white/10 flex items-center justify-between gap-2">
          <div className="font-bold tracking-tight text-sm flex items-center gap-2"><History className="w-4 h-4 text-[#A3E635]" /> {tr("Vollbackup-Historie")}</div>
          <div className="text-[11px] text-gray-500">{tr("{n} Einträge").replace("{n}", allBackups.length)}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-gray-500 border-b border-white/5">
                <th className="text-left font-semibold px-5 py-2.5">{tr("Datum / Uhrzeit")}</th>
                <th className="text-left font-semibold px-3 py-2.5">{tr("Typ")}</th>
                <th className="text-left font-semibold px-3 py-2.5">{tr("Status")}</th>
                <th className="text-left font-semibold px-3 py-2.5">{tr("Grösse")}</th>
                <th className="text-left font-semibold px-3 py-2.5 hidden lg:table-cell">{tr("Erstellt von")}</th>
                <th className="text-left font-semibold px-3 py-2.5 hidden xl:table-cell">{tr("Prüfsumme (SHA-256)")}</th>
                <th className="text-left font-semibold px-3 py-2.5 hidden lg:table-cell">{tr("Speicherort")}</th>
                <th className="text-right font-semibold px-5 py-2.5">{tr("Aktionen")}</th>
              </tr>
            </thead>
            <tbody>
              {allBackups.length === 0 && <tr><td colSpan={8} className="px-5 py-6 text-gray-500">{tr("Noch keine Backups vorhanden.")}</td></tr>}
              {pageItems.map((b) => (
                <React.Fragment key={b.id}>
                  <tr className="border-b border-white/5" data-testid={`backup-row-${b.id}`}>
                    <td className="px-5 py-3 whitespace-nowrap">{smartDate(b.created_at)}</td>
                    <td className="px-3 py-3"><span className="text-xs px-2 py-0.5 rounded-full bg-white/5 border border-white/10 whitespace-nowrap">{tr(TYPE_LABEL[b.type] || b.type)}</span></td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold ${b.status === "success" ? "text-[#A3E635]" : "text-red-400"}`}>
                        {b.status === "success" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                        {tr(STATUS_LABEL[b.status] || b.status)}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-gray-400 whitespace-nowrap">{fmtSize(b.size_bytes)}</td>
                    <td className="px-3 py-3 text-gray-400 truncate max-w-[140px] hidden lg:table-cell">{b.created_by_name}</td>
                    <td className="px-3 py-3 hidden xl:table-cell">
                      {b.checksum_sha256 ? (
                        <button onClick={() => { navigator.clipboard?.writeText(b.checksum_sha256); toast.success(tr("Prüfsumme kopiert")); }}
                          data-testid={`backup-checksum-${b.id}`} title={b.checksum_sha256}
                          className="font-mono text-[11px] text-gray-400 hover:text-white flex items-center gap-1.5 transition-colors">
                          {b.checksum_sha256.slice(0, 12)}… <Copy className="w-3 h-3" />
                        </button>
                      ) : <span className="text-gray-600 text-xs">—</span>}
                    </td>
                    <td className="px-3 py-3 text-gray-500 text-xs whitespace-nowrap hidden lg:table-cell">{b.storage_location || tr("Lokal")}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setDetailsId(detailsId === b.id ? null : b.id)} data-testid={`backup-details-${b.id}`}
                          className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors" title={tr("Details anzeigen")}><ListChecks className="w-4 h-4" /></button>
                        {b.status === "success" && (
                          <button onClick={() => openDownload(b.id)} data-testid={`backup-dl-${b.id}`}
                            className="p-2 rounded-lg text-gray-400 hover:text-[#A3E635] hover:bg-white/5 transition-colors" title={tr("Sicher herunterladen")}><Download className="w-4 h-4" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {detailsId === b.id && (
                    <tr className="bg-white/[0.02]" data-testid={`backup-details-row-${b.id}`}>
                      <td colSpan={8} className="px-5 py-3">
                        <div className="text-xs text-gray-400 mb-2">{b.note || tr("Backup-Details")} · {tr("App-Version {x}").replace("{x}", b.app_version || "—")} · {b.encrypted ? tr("verschlüsselt (AES-256-GCM)") : tr("unverschlüsselt")}</div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs text-gray-300">
                          {Object.entries(b.counts || {}).map(([k, v]) => (
                            <div key={k} className="flex justify-between"><span className="text-gray-500">{tr(COLL_LABEL[k] || k)}</span><span className="font-mono">{v}</span></div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="px-5 py-3 border-t border-white/10 flex items-center justify-between gap-2" data-testid="backup-history-pagination">
            <div className="text-[11px] text-gray-500">{tr("Seite {a} von {b}").replace("{a}", curPage).replace("{b}", totalPages)}</div>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={curPage <= 1} data-testid="history-prev"
                className="bg-white/5 hover:bg-white/10 border border-white/10 py-1.5 px-3 rounded-lg text-xs flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                <ChevronLeft className="w-3.5 h-3.5" /> {tr("Zurück")}
              </button>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={curPage >= totalPages} data-testid="history-next"
                className="bg-white/5 hover:bg-white/10 border border-white/10 py-1.5 px-3 rounded-lg text-xs flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                {tr("Weiter")} <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* ===== 3 Info-Karten ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="bg-amber-500/[0.06] border border-amber-500/25 rounded-xl p-5" data-testid="info-security">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-amber-300 mb-3"><AlertTriangle className="w-4 h-4" /> {tr("Wichtige Sicherheitshinweise")}</div>
          <ul className="space-y-2 text-xs text-amber-200/90">
            <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-300" /> {tr("Vor jeder Wiederherstellung wird automatisch ein Notfall-Backup erstellt.")}</li>
            <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-300" /> {tr("Stellen Sie nur Backups mit kompatibler App-Version und gültiger Prüfsumme wieder her.")}</li>
            <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-300" /> {tr("Alle bestehenden Daten werden überschrieben und können nicht wiederhergestellt werden.")}</li>
            <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-300" /> {tr("Grün erscheint nur bei erfolgreich geprüften Backups.")}</li>
          </ul>
        </div>

        <div className="bg-[#121217] border border-white/10 rounded-xl p-5" data-testid="info-checklist">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-gray-400 mb-3"><ListChecks className="w-4 h-4 text-[#A3E635]" /> {tr("Wiederherstellungs-Checkliste")}</div>
          <ul className="space-y-2 text-xs text-gray-300">
            <li className="flex items-start gap-2"><span className="text-[#A3E635] font-bold">1.</span> {tr("Backup-Datei prüfen (Prüfsumme SHA-256)")}</li>
            <li className="flex items-start gap-2"><span className="text-[#A3E635] font-bold">2.</span> {tr("Test-Wiederherstellung in der Sandbox ausführen")}</li>
            <li className="flex items-start gap-2"><span className="text-[#A3E635] font-bold">3.</span> {tr("Notfall-Backup wird automatisch erstellt")}</li>
            <li className="flex items-start gap-2"><span className="text-[#A3E635] font-bold">4.</span> {tr("Mit Passwort und Zwei-Faktor bestätigen")}</li>
          </ul>
          <div className="text-[11px] text-gray-500 mt-3 pt-3 border-t border-white/10">{tr("Wiederherstellung nur nach Prüfung und Super-Admin-Freigabe.")}</div>
        </div>

        <div className="bg-[#121217] border border-white/10 rounded-xl p-5 flex flex-col" data-testid="info-audit">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-gray-400 mb-3"><FileSearch className="w-4 h-4 text-[#A3E635]" /> {tr("Audit & Protokollzugriff")}</div>
          <p className="text-xs text-gray-300 flex-1">{tr("Alle Backup- und Wiederherstellungs-Aktionen werden im Audit-Log erfasst.")}</p>
          <div className="text-[11px] text-gray-500 mt-2">{tr("Zugriff nur für Super Admin.")}</div>
          <button onClick={() => nav("/admin/audit-log")} data-testid="info-audit-link"
            className="mt-3 w-full bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm font-semibold py-2 rounded-lg flex items-center justify-center gap-2 transition-colors">
            <FileSearch className="w-4 h-4" /> {tr("Audit-Log öffnen")}
          </button>
        </div>
      </div>

      {/* === Download-Sicherheitsmodal === */}
      {dlModal && (
        <Modal title={tr("Sicherheitsbestätigung für Download")} icon={ShieldAlert} onClose={() => !dlBusy && setDlModal(null)} testid="download-modal">
          <p className="text-sm text-gray-400 mb-4">{tr("Die Backup-Datei enthält sensible System- und Kundendaten (Secrets sind AES-256-verschlüsselt). Bitte bestätigen Sie Ihre Identität.")}</p>
          <Field label={tr("Ihr Passwort")}><input type="password" value={dlPw} onChange={(e) => setDlPw(e.target.value)} data-testid="download-password" autoComplete="current-password" className="inp" placeholder={tr("Passwort")} /></Field>
          <Field label={tr("Zwei-Faktor-Code (falls aktiv)")}><input value={dlCode} onChange={(e) => setDlCode(e.target.value)} data-testid="download-code" inputMode="numeric" maxLength={6} className="inp tracking-widest" placeholder={tr("6-stelliger Code")} /></Field>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setDlModal(null)} disabled={dlBusy} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{tr("Abbrechen")}</button>
            <button onClick={runDownload} disabled={dlBusy || !dlPw} data-testid="download-confirm"
              className="bg-[#A3E635] text-black font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50">
              {dlBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {tr("Sicher herunterladen")}
            </button>
          </div>
        </Modal>
      )}

      {/* === Restore-Point Wiederherstellungs-Modal === */}
      {rpRestore && (
        <Modal title={tr("Aus Restore-Point wiederherstellen")} icon={RotateCcw} iconColor="text-amber-400" onClose={() => !rpRestoreBusy && setRpRestore(null)} testid="rp-restore-modal">
          <div className="flex items-start gap-2 text-xs text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2.5 mb-4">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><span>{tr("Der aktuelle Stand wird durch diesen Restore-Point ersetzt. Vorher wird automatisch ein Sicherungspunkt erstellt. Erfordert Passwort und Zwei-Faktor (falls aktiv).")}</span>
          </div>
          <Field label={<>{tr("Tippen Sie zur Bestätigung")} <b className="text-white">WIEDERHERSTELLEN</b></>}>
            <input value={rpConfirm} onChange={(e) => setRpConfirm(e.target.value)} data-testid="rp-restore-confirm-text" autoComplete="off" placeholder="WIEDERHERSTELLEN" className="inp tracking-widest" />
          </Field>
          <Field label={tr("Ihr Passwort")}><input type="password" value={rpPw} onChange={(e) => setRpPw(e.target.value)} data-testid="rp-restore-password" autoComplete="current-password" placeholder={tr("Passwort")} className="inp" /></Field>
          <Field label={tr("Zwei-Faktor-Code (falls aktiv)")}><input value={rpCode} onChange={(e) => setRpCode(e.target.value)} data-testid="rp-restore-code" inputMode="numeric" maxLength={6} placeholder={tr("6-stelliger Code")} className="inp tracking-widest" /></Field>
          <label className="flex items-start gap-2 text-sm text-gray-300 mt-1 mb-2 cursor-pointer">
            <input type="checkbox" checked={rpUnderstand} onChange={(e) => setRpUnderstand(e.target.checked)} data-testid="rp-restore-understand" className="mt-0.5 accent-[#A3E635]" />
            <span>{tr("Ich verstehe, dass bestehende Daten überschrieben werden.")}</span>
          </label>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setRpRestore(null)} disabled={rpRestoreBusy} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{tr("Abbrechen")}</button>
            <button onClick={runRpRestore} disabled={rpRestoreBusy || rpConfirm.trim().toUpperCase() !== "WIEDERHERSTELLEN" || !rpPw || !rpUnderstand} data-testid="rp-restore-confirm"
              className="bg-amber-500 hover:bg-amber-400 text-black font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50">
              {rpRestoreBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} {rpRestoreBusy ? tr("Wird wiederhergestellt…") : tr("Wiederherstellen")}
            </button>
          </div>
        </Modal>
      )}

      {/* === Restore-Assistent (4 Schritte) === */}
      <input ref={fileRef} type="file" accept="application/json,.json" onChange={onFile} className="hidden" data-testid="restore-file-input" />
      {wizard > 0 && (
        <Modal title={tr("Aus Backup wiederherstellen")} icon={Upload} iconColor="text-amber-400" onClose={closeWizard} testid="restore-wizard" wide>
          <div className="flex items-center gap-1.5 mb-5 text-xs flex-wrap">
            {[[1, tr("Datei wählen")], [2, tr("Prüfen")], [3, tr("Test / Dry-Run")], [4, tr("Bestätigen")]].map(([n, lbl], i) => (
              <React.Fragment key={n}>
                <div className={`flex items-center gap-1.5 ${wizard >= n ? "text-[#A3E635]" : "text-gray-600"}`}>
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${wizard >= n ? "bg-[#A3E635] text-black" : "bg-white/10 text-gray-500"}`}>{n}</span>
                  <span className="hidden sm:inline">{lbl}</span>
                </div>
                {i < 3 && <ChevronRight className="w-3.5 h-3.5 text-gray-600" />}
              </React.Fragment>
            ))}
          </div>

          {wizard === 1 && (
            <div className="text-center py-6" data-testid="restore-step1">
              <FileCheck2 className="w-10 h-10 text-gray-500 mx-auto mb-3" />
              <p className="text-sm text-gray-400 mb-4">{tr("Bitte wählen Sie eine zuvor heruntergeladene Backup-Datei (.json) aus.")}</p>
              <button onClick={() => fileRef.current?.click()} data-testid="restore-choose-btn"
                className="bg-white/5 hover:bg-white/10 border border-white/10 py-2.5 px-5 rounded-lg text-sm inline-flex items-center gap-2"><Upload className="w-4 h-4" /> {tr("Backup-Datei wählen")}</button>
            </div>
          )}

          {wizard === 2 && (
            <div data-testid="restore-step2">
              <div className="text-sm font-semibold mb-0.5">{restoreFile?.name}</div>
              <div className="text-xs text-gray-500 mb-4">{tr("Backup vom {x} · App-Version {y}").replace("{x}", smartDate(payload?.created_at)).replace("{y}", payload?.app_version || "—")}</div>
              {busy && <div className="flex items-center gap-2 text-sm text-gray-400 py-4"><Loader2 className="w-4 h-4 animate-spin" /> {tr("Backup wird geprüft…")}</div>}
              {verifyRes && (
                <div className="space-y-2 mb-4" data-testid="restore-verify-result">
                  <CheckRow ok={verifyRes.version_compatible} label={tr("Kompatible App-Version")} />
                  <CheckRow ok={verifyRes.checksum_valid !== false} warn={verifyRes.checksum_valid === null} label={tr("Prüfsumme gültig (SHA-256)")} />
                  <CheckRow ok={(verifyRes.corrupt_records || 0) === 0} warn={(verifyRes.corrupt_records || 0) > 0} label={tr("Keine beschädigten Datensätze")} />
                  <CheckRow ok={Object.keys(verifyRes.counts || {}).length > 0} label={tr("Bekannte KEWAGPage-Daten gefunden")} />
                  {verifyRes.errors?.map((e, i) => <div key={i} className="text-xs text-red-300 flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{e}</div>)}
                  {verifyRes.warnings?.map((w, i) => <div key={i} className="text-xs text-amber-300 flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{w}</div>)}
                </div>
              )}
              <div className="flex justify-end gap-2 mt-5">
                <button onClick={closeWizard} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{tr("Abbrechen")}</button>
                <button onClick={() => setWizard(3)} disabled={!verifyRes || !verifyRes.valid} data-testid="restore-to-dryrun"
                  className="bg-amber-500/90 hover:bg-amber-400 text-black font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50">{tr("Weiter zum Test")} <ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}

          {wizard === 3 && (
            <div data-testid="restore-step3">
              <p className="text-sm text-gray-400 mb-3">{tr("Trockenlauf: Diese Daten würden wiederhergestellt. Optional können Sie den Import zuerst sicher in einer Sandbox testen.")}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                {(verifyRes?.conflicts || []).slice(0, 9).map((c) => (
                  <div key={c.collection} className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-xs" data-testid={`dryrun-${c.collection}`}>
                    <div className="text-gray-400">{tr(c.label)}</div>
                    <div className="font-mono"><span className="text-gray-500">{c.current}</span> <ChevronRight className="w-3 h-3 inline text-gray-600" /> <span className="font-semibold">{c.incoming}</span></div>
                  </div>
                ))}
              </div>
              {(verifyRes?.conflicts || []).length > 0 && (
                <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 mb-4 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {tr("Konflikt: {n} Sammlung(en) enthalten bereits Daten und werden vollständig ersetzt.").replace("{n}", verifyRes.conflicts.length)}
                </div>
              )}
              <button onClick={runSandbox} disabled={busy} data-testid="restore-sandbox-btn"
                className="w-full mb-3 bg-blue-500/15 hover:bg-blue-500/25 border border-blue-500/30 text-blue-200 py-2.5 rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />} {tr("Test-Wiederherstellung in Sandbox ausführen")}
              </button>
              {sandboxRes && (
                <div className={`text-xs rounded-lg px-3 py-2.5 mb-4 ${sandboxRes.ok ? "bg-[#A3E635]/10 border border-[#A3E635]/25 text-[#A3E635]" : "bg-red-500/10 border border-red-500/25 text-red-300"}`} data-testid="sandbox-result">
                  {sandboxRes.ok ? <><CheckCircle2 className="w-4 h-4 inline mr-1.5" />{tr("Sandbox-Test erfolgreich: {n} Datensätze importierbar – Live-Daten unberührt.").replace("{n}", sandboxRes.total)}</> : <>{tr("Sandbox-Test mit Fehlern: {x}").replace("{x}", JSON.stringify(sandboxRes.errors).slice(0, 120))}</>}
                </div>
              )}
              <div className="flex justify-end gap-2 mt-2">
                <button onClick={() => setWizard(2)} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{tr("Zurück")}</button>
                <button onClick={() => setWizard(4)} data-testid="restore-to-confirm"
                  className="bg-amber-500/90 hover:bg-amber-400 text-black font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2">{tr("Zur echten Wiederherstellung")} <ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}

          {wizard === 4 && (
            <div data-testid="restore-step4">
              <div className="flex items-start gap-2 text-xs text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2.5 mb-4">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><span>{tr("Diese Aktion kann nicht rückgängig gemacht werden. Vorher wird automatisch ein Notfall-Backup erstellt. Erfordert Passwort und Zwei-Faktor (falls aktiv).")}</span>
              </div>
              <Field label={<>{tr("Tippen Sie zur Bestätigung")} <b className="text-white">WIEDERHERSTELLEN</b></>}>
                <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} data-testid="restore-confirm-text" autoComplete="off" placeholder="WIEDERHERSTELLEN" className="inp tracking-widest" />
              </Field>
              <Field label={tr("Ihr Passwort")}><input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="restore-password" autoComplete="current-password" placeholder={tr("Passwort")} className="inp" /></Field>
              <Field label={tr("Zwei-Faktor-Code (falls aktiv)")}><input value={code} onChange={(e) => setCode(e.target.value)} data-testid="restore-code" inputMode="numeric" maxLength={6} placeholder={tr("6-stelliger Code")} className="inp tracking-widest" /></Field>
              <label className="flex items-start gap-2 text-sm text-gray-300 mt-1 mb-2 cursor-pointer">
                <input type="checkbox" checked={understand} onChange={(e) => setUnderstand(e.target.checked)} data-testid="restore-understand" className="mt-0.5 accent-[#A3E635]" />
                <span>{tr("Ich verstehe, dass bestehende Daten überschrieben werden.")}</span>
              </label>
              <div className="flex justify-end gap-2 mt-5">
                <button onClick={() => setWizard(3)} disabled={busy} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">{tr("Zurück")}</button>
                <button onClick={runRestore} disabled={busy || confirmText.trim().toUpperCase() !== "WIEDERHERSTELLEN" || !pw || !understand} data-testid="restore-final-btn"
                  className="bg-red-500 hover:bg-red-400 text-white font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {busy ? tr("Wird wiederhergestellt…") : tr("Wiederherstellung endgültig starten")}
                </button>
              </div>
            </div>
          )}
        </Modal>
      )}

      <style>{`.inp{width:100%;background:#000;border:1px solid rgba(255,255,255,.1);border-radius:.5rem;padding:.5rem .75rem;font-size:.875rem;color:#fff;outline:none}.inp:focus{border-color:#A3E635}`}</style>
    </div>
  );
}

const TILE_ACCENT = { lime: "text-[#A3E635]", amber: "text-amber-400", red: "text-red-400", gray: "text-gray-300", default: "text-white" };
const READY_BOX = { green: "bg-[#A3E635]/10", amber: "bg-amber-500/10", red: "bg-red-500/10", gray: "bg-white/5" };
const checkState = (v) => (v === true ? "ok" : v === false ? "fail" : "none");

function IntegrityCheck({ label, state }) {
  const icon = state === "ok" ? <CheckCircle2 className="w-4 h-4 text-[#A3E635]" />
    : state === "fail" ? <X className="w-4 h-4 text-red-400" />
      : <Clock className="w-4 h-4 text-gray-500" />;
  const cls = state === "ok" ? "text-gray-200" : state === "fail" ? "text-red-300" : "text-gray-500";
  return (
    <div className="flex items-center gap-2 text-sm py-0.5">
      <span className="shrink-0">{icon}</span>
      <span className={cls}>{label}</span>
    </div>
  );
}

function StatusTile({ icon: Icon, label, value, sub, accent = "default", pill, tone = "gray", testid }) {
  const pillCls = (WP_TONE[tone] || WP_TONE.gray).chip;
  return (
    <div className="bg-[#121217] border border-white/10 rounded-xl p-5 hover:border-white/20 transition-colors" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-gray-400">{Icon && <Icon className="w-4 h-4 shrink-0" />} <span className="truncate">{label}</span></div>
        {pill && <span className={`text-[10px] px-2 py-0.5 rounded-full border shrink-0 ${pillCls}`}>{pill}</span>}
      </div>
      <div className={`text-lg font-bold truncate ${TILE_ACCENT[accent]}`}>{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

function Panel({ icon: Icon, title, children, testid }) {
  return (
    <div className="bg-[#121217] border border-white/10 rounded-xl p-5 flex flex-col" data-testid={testid}>
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-gray-400 mb-3">{Icon && <Icon className="w-4 h-4 text-[#A3E635]" />} {title}</div>
      {children}
    </div>
  );
}

const TONES = {
  lime: { box: "bg-[#A3E635]/10", icon: "text-[#A3E635]", btn: "bg-[#A3E635] text-black" },
  amber: { box: "bg-amber-500/10", icon: "text-amber-400", btn: "bg-amber-500/90 hover:bg-amber-400 text-black" },
  blue: { box: "bg-blue-500/10", icon: "text-blue-300", btn: "bg-blue-500/20 hover:bg-blue-500/30 text-blue-100 border border-blue-500/30" },
  default: { box: "bg-white/5", icon: "text-gray-200", btn: "bg-white/5 hover:bg-white/10 border border-white/10 text-white" },
};
function ActionCard({ icon: Icon, tone = "default", title, desc, btn, busy, onClick, testid }) {
  const t = TONES[tone];
  return (
    <div className={`bg-[#121217] border rounded-xl p-5 flex flex-col ${tone === "amber" ? "border-amber-500/30" : "border-white/10"}`} data-testid={`${testid}-card`}>
      <div className={`w-10 h-10 rounded-xl ${t.box} flex items-center justify-center mb-3`}><Icon className={`w-5 h-5 ${t.icon}`} /></div>
      <h3 className="font-bold tracking-tight text-sm">{title}</h3>
      <p className="text-xs text-gray-400 mt-1 flex-1">{desc}</p>
      <button onClick={onClick} disabled={busy} data-testid={`${testid}-btn`}
        className={`mt-4 font-bold py-2.5 px-4 rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-60 transition-colors ${t.btn}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />} {btn}
      </button>
    </div>
  );
}

function KV({ k, v, good }) {
  return <div className="flex items-center justify-between gap-2 text-xs py-0.5"><span className="text-gray-500">{k}</span><span className={`text-right ${good ? "text-[#A3E635] font-semibold" : "text-gray-200 font-medium"}`}>{v}</span></div>;
}
function CheckRow({ ok, warn, label }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {ok && !warn ? <CheckCircle2 className="w-4 h-4 text-[#A3E635]" /> : warn ? <AlertTriangle className="w-4 h-4 text-amber-400" /> : <X className="w-4 h-4 text-red-400" />}
      <span className={ok && !warn ? "text-gray-200" : warn ? "text-amber-300" : "text-red-300"}>{label}</span>
    </div>
  );
}
function Field({ label, children }) {
  return <div className="mb-3"><label className="text-xs text-gray-400 mb-1 block">{label}</label>{children}</div>;
}
function Modal({ title, icon: Icon, iconColor = "text-[#A3E635]", onClose, children, testid, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full ${wide ? "max-w-xl" : "max-w-md"} bg-[#121217] border border-white/10 rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#121217] z-10">
          <h3 className="font-bold flex items-center gap-2">{Icon && <Icon className={`w-5 h-5 ${iconColor}`} />} {title}</h3>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
